import { apiClient } from './client';

// Types for Checkout API responses
export interface ValidateCheckoutSessionSuccessData {
  success: true;
  bizToken: string | null;
  faceIndex: string | null;
  orderDetails: {
    orderId: string;
    amount: number;
    vendorName: string;
  };
  /** Whether a payment method is required to complete the checkout */
  payment_required: boolean;
  /** Phone + country code pre-filled from similar-face OTP verification */
  prefill?: {
    phone_number: string;
    country_code: string;
  };
  /** Short-lived JWT — phone already verified; skip OTP in registration */
  phoneVerificationToken?: string;
}

export interface ValidateCheckoutSessionErrorData {
  success: false;
  error: {
    code: string;
    message: string;
  };
}

export type ValidateCheckoutSessionResponse =
  | ValidateCheckoutSessionSuccessData
  | ValidateCheckoutSessionErrorData;

// Backend wraps responses in this structure
interface ApiResponse<T> {
  success: boolean;
  status: number;
  data: T;
}

/**
 * Validate a checkout_session_id from the Checkout flow
 * This calls aye-user-backend to validate the checkout session
 * and retrieve the biz_token from the already-completed face verification
 *
 * @param checkout_session_id - The checkout_session_id from the URL query parameter
 * @returns The validation result with biz_token if valid
 */
export const validateCheckoutSession = async (
  checkout_session_id: string
): Promise<ValidateCheckoutSessionResponse> => {
  try {
    const response = await apiClient.get<
      ApiResponse<ValidateCheckoutSessionResponse>
    >(`/checkout/validate`, {
      params: { checkout_session_id }
    });
    // Backend wraps response in { success, status, data }, so unwrap it
    return response.data.data;
  } catch (error: any) {
    // Handle axios errors - backend wraps errors in { success, status, data }
    if (error.response?.data?.data) {
      return error.response.data.data as ValidateCheckoutSessionResponse;
    }
    return {
      success: false,
      error: {
        code: 'NETWORK_ERROR',
        message: 'Failed to validate checkout session. Please try again.'
      }
    };
  }
};
